import { ReactNode, useCallback, useId } from 'react';
import { useClientsListQuery } from '@/modules/common/queries/clients.ts';
import { SelectPaginate, SelectPaginateOption } from 'ui';

type baseProps = {
  label?: string;
  placeholder?: string;
  formFieldTitle?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  isClearable?: boolean;
};

type singleProps = {
  isMulti: false;
  value?: SelectPaginateOption<number>;
  onChange: (option: SelectPaginateOption<number> | null, title?: string) => void;
  defaultValue?: SelectPaginateOption<number>;
};

type multiProps = {
  isMulti: true;
  value?: SelectPaginateOption<number>[];
  onChange: (options: SelectPaginateOption<number>[], title?: string) => void;
  defaultValue?: SelectPaginateOption<number>[];
};

type ClientsSelectPaginatedProps = baseProps & (singleProps | multiProps);

const ClientsSelectPaginated = ({
  label = 'Client (optional)',
  error,
  value,
  isMulti,
  className,
  placeholder,
  defaultValue,
  formFieldTitle,
  onChange,
  disabled = false,
  isClearable = false,
}: ClientsSelectPaginatedProps) => {
  const { fetchNextPage } = useClientsListQuery(
    {
      page: 1,
      page_size: 15,
      filter_by_name: '',
    },
    true,
    { cacheKey: useId() },
  );

  const handleFetchNextClients = async (
    page: number,
    search?: string,
  ): Promise<SelectPaginateOption<number>[]> => {
    const clientsData = await fetchNextPage({ pageParam: { page, search } });

    const clients = clientsData?.data?.pages?.at(-1)?.data || [];

    return clients.map(({ id, name }) => ({ label: name as ReactNode, value: id }));
  };

  const handleChange = useCallback(
    (options: SelectPaginateOption<number> | SelectPaginateOption<number>[] | null) => {
      if (!isMulti && options === null) {
        onChange(null as any, formFieldTitle);
        return;
      }

      if (Array.isArray(options) && options.length === 0) {
        onChange(options as any, formFieldTitle);
        return;
      }

      if (!Array.isArray(options) && (!options || !options.value)) return;
      if (Array.isArray(options) && options.some(({ value }) => !value)) return;

      onChange(options as any, formFieldTitle);
    },
    [formFieldTitle, isMulti, onChange],
  );

  return (
    <SelectPaginate
      autoHeight
      placeholder={placeholder}
      isMulti={isMulti}
      disabled={disabled}
      defaultValue={defaultValue}
      error={error}
      value={value || null}
      className={className}
      onChange={handleChange}
      label={label}
      onAsync={handleFetchNextClients}
      debounceTimeoutSearch={700}
      isClearable={isClearable}
    />
  );
};

export default ClientsSelectPaginated;
